<template>
    <div class="widget-config-auto-complete">
        <RedesignFormLabel
            :label="templateParameterHelpName"
            :info-text="templateParameterHelpDescription"
            class="mb-4"
        />
        <RedesignRadioGroup
            v-model="entityType"
            class="radio-groups flex flex-col gap-3"
            @input="updateEntityType"
        >
            <RedesignRadio
                :label="ENTITY_SELECTION_TYPE.POSITIVE"
            >
                {{ $t('autocomplete.includeTheSelected') }}
            </RedesignRadio>
            <RedesignRadio
                :label="ENTITY_SELECTION_TYPE.NEGATIVE"
            >
                {{ $t('autocomplete.excludeTheSelected') }}
            </RedesignRadio>
        </RedesignRadioGroup>
        <div class="pt-5">
            <RedesignBaseSelect
                ref="selectRef"
                :loading="loading"
                :collapse-tags="true"
                :value="autocompleteValue"
                :data="options"
                :config="{
                    labelKey: templateConfig.label || 'label',
                    valueKey: templateConfig.value || 'value',
                    secondLabelKey: templateConfig.second_label || 'second_label'
                }"
                multiple
                full-width
                filterable
                @change="onAutocompleteChange"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { WidgetConfig, WidgetParameterValueJson } from '@/types/dashboard'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import { computed, onMounted, ref, nextTick, onBeforeUnmount } from 'vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadio from '@/components/RedesignBaseComponents/Inputs/RedesignRadio.vue'
import cloneDeep from 'lodash/cloneDeep'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import get from 'lodash/get'
import { getOptionsList, getTemplateConfig } from '@/helpers/entitiesList'
import { ENTITY_TYPE_AUTOMATIC, ENTITY_TYPE_IVR, entityTypesList } from '@/enum/widgetTemplateConfigs'
import { CAMPAIGN_IVR_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import {
    getTemplateParameterHelpDescription,
    getTemplateParameterHelpName
} from '@/components/RedesignComponents/Widgets/widget.helper'
import {
    ENTITY_SELECTION_TYPE,
    resolveEntitySelectionType,
    isEntitySelectionType,
    type EntitySelectionType,
} from '@/enum/entitySelection'

interface OptionsConfig {
    [key: string]: Record<string, number>
}

const props = defineProps<{
    value: WidgetConfig
}>()

const emit = defineEmits([ 'input', 'update:value' ])

const loading = ref(true)
const entityType = ref<EntitySelectionType>(ENTITY_SELECTION_TYPE.NEGATIVE)
const localWidgetParameterValueJson = ref<WidgetParameterValueJson>({})
const options = ref([])
const selectRef = ref(null)
let inputEl: HTMLInputElement | null = null

const model = computed({
    get () {
        return props.value
    },
    set (val) {
        emit('input', val)
        emit('update:value', val)
    }
})
const templateConfig = computed(() => {
    return getTemplateConfig(props.value.ParameterName)
})
const autocompleteValue = computed(() => {
    if (model.value.WidgetParameterJson === 1) {
        return get(model.value.WidgetParameterValueJson, entityType.value, {})
    } else {
        return get(JSON.parse(model.value.WidgetParameterValue || ''), entityType.value, [])
    }
})
const templateParameterHelpName = computed(() => {
    return getTemplateParameterHelpName(props.value)
})
const templateParameterHelpDescription = computed(() => {
    return getTemplateParameterHelpDescription(props.value)
})

function checkIfIsEntityType (entity: string): entity is EntitySelectionType {
    return isEntitySelectionType(entity)
}

function updateEntityType (type: string) {
    if (!model.value.WidgetParameterValueJson) {
        model.value.WidgetParameterValueJson = {}
    }
    const parameterValueJson = model.value.WidgetParameterValueJson

    // Save the entity type selection so it persists even when lists are empty
    if (isEntitySelectionType(type)) {
        parameterValueJson.EntityTypeSelection = type
    }

    Object.keys(parameterValueJson).forEach((key) => {
        if (checkIfIsEntityType(key)) {
            if (key !== type) {
                const valueJson = parameterValueJson[key] || []
                localWidgetParameterValueJson.value[key] = cloneDeep(valueJson)
                parameterValueJson[key] = []
            } else {
                parameterValueJson[key] = localWidgetParameterValueJson.value[key] || []
            }
        }
    })
    emit('update:value', model.value)
}

function onAutocompleteChange (data: Array<number>) {
    if (model.value.WidgetParameterJson === 1) {
        if (!model.value.WidgetParameterValueJson) {
            model.value.WidgetParameterValueJson = {}
        }
        model.value.WidgetParameterValueJson[entityType.value] = cloneDeep(data)
        model.value.WidgetParameterValueJson.EntityTypeSelection = entityType.value
        localWidgetParameterValueJson.value[entityType.value] = cloneDeep(data)
        const anotherNonActiveType = entityType.value === ENTITY_SELECTION_TYPE.POSITIVE
            ? ENTITY_SELECTION_TYPE.NEGATIVE
            : ENTITY_SELECTION_TYPE.POSITIVE
        model.value.WidgetParameterValueJson[anotherNonActiveType] = []
        localWidgetParameterValueJson.value[anotherNonActiveType] = []
    } else {
        const currentValue = JSON.parse(model.value.WidgetParameterValue || '')
        currentValue[entityType.value] = cloneDeep(data)

        model.value.WidgetParameterValue = JSON.stringify(data)
    }
    emit('update:value', model.value)
}

async function getData () {
    if (props.value.EntityTypeID === entityTypesList[ENTITY_TYPE_AUTOMATIC]) {
        const fetchedOptions = await getOptionsList(props.value.ParameterName)

        options.value = fetchedOptions.filter((el: {
            camp_type: number
        }) => el.camp_type === 5)
    } else if (props.value.EntityTypeID === entityTypesList[ENTITY_TYPE_IVR] || props.value.ParameterName === CAMPAIGN_IVR_LIST_PARAMETER_NAME) {
        const fetchedOptions = await getOptionsList(props.value.ParameterName)

        options.value = fetchedOptions.filter((el: {
            camp_type: number
        }) => el.camp_type === 3)
    } else {
        options.value = await getOptionsList(props.value.ParameterName)
    }
}

function setActiveEntityTypeByData () {
    const parameterValueJson = props.value.WidgetParameterValueJson
    if (!parameterValueJson) {
        return
    }

    // First, restore local state for both entity types
    Object.keys(parameterValueJson).forEach(key => {
        if (checkIfIsEntityType(key)) {
            const valueJson = parameterValueJson[key] || []
            localWidgetParameterValueJson.value[key] = cloneDeep(valueJson)
        }
    })

    // Determine which mode to use:
    // 1. If EntityTypeSelection is saved, use it (remembers user's choice even when lists are empty)
    // 2. Otherwise, auto-detect based on which list has data (backward compatibility)
    // 3. If nothing selected, default to "Exclude"
    entityType.value = resolveEntitySelectionType(
        parameterValueJson.EntityTypeSelection,
        parameterValueJson[ENTITY_SELECTION_TYPE.POSITIVE],
        parameterValueJson[ENTITY_SELECTION_TYPE.NEGATIVE]
    )
}

const optionsConfig = computed(() => {
    if (!options.value?.length) {
        return {}
    }
    return options.value.reduce<OptionsConfig>((acc, item) => {
        acc[item[templateConfig.value.label]] = item
        acc[item[templateConfig.value.value]] = item
        return acc
    }, {})
})

const onPaste = (e: ClipboardEvent) => {
    e.preventDefault()
    const pastedText = e.clipboardData?.getData('text')?.trim()
    if (!pastedText) return

    const pastedItems = pastedText.split(',').map(item => item.trim())

    const data = pastedItems.reduce<number[]>((acc, item) => {
        const option = optionsConfig.value[item]
        if (option) {
            const value = option[templateConfig.value.value]
            if (value !== undefined) acc.push(value)
        }
        return acc
    }, [])


    const savedData: number[] = localWidgetParameterValueJson.value?.[entityType.value] ?? []

    if (data.length) {
        onAutocompleteChange([ ...new Set([ ...data, ...savedData ]) ])
    }
}

function initData () {
    getData()
    setActiveEntityTypeByData()
    loading.value = false
    nextTick(() => {
        if (selectRef.value) {
            inputEl = selectRef.value?.$el.querySelector('input')
            if (inputEl) {
                inputEl.addEventListener('paste', onPaste)
            }
        }
    })
}

onMounted(initData)

onBeforeUnmount(() => {
    if (inputEl) {
        inputEl.removeEventListener('paste', onPaste)
    }
})
</script>
